import { useEffect, useState } from "react";
import { useParams } from "react-router";
import type { DuviSharedAdminStatusPillStatus } from "@duvi-agency/ui";
import { tickets as ticketsApi } from "../../api/client";
import { useDuviCoreAdminQuery } from "../../hooks/use-api";
import { useDuviCoreAdminToast } from "../../components/duvi-core-admin-kit";

export const DUVI_CORE_ADMIN_TICKET_STATUS_SELECT = [
  "open",
  "in-progress",
  "awaiting",
  "resolved",
  "closed",
];
export const DUVI_CORE_ADMIN_TICKET_PRIORITY_SELECT = [
  "urgent",
  "high",
  "medium",
  "low",
];

const PRIORITY_COLOR: Record<string, string> = {
  urgent: "var(--error)",
  high: "var(--accent)",
  medium: "var(--info)",
  low: "var(--text-muted)",
};

export function duviCoreAdminDetailPriorityColor(p: string): string {
  return PRIORITY_COLOR[(p || "").toLowerCase()] ?? "var(--text-muted)";
}

function normaliseStatus(s: string): string {
  return (s || "").replace(/_/g, "-");
}

export function duviCoreAdminDetailStatusTone(
  status: string,
): DuviSharedAdminStatusPillStatus {
  switch (normaliseStatus(status)) {
    case "in-progress":
      return "warning";
    case "resolved":
      return "success";
    case "closed":
      return "inactive";
    default:
      return "info";
  }
}

export function duviCoreAdminCap(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// Preserves the existing role predicate — the platform-admin side.
export function duviCoreAdminIsAdminRole(role: string): boolean {
  return (
    role === "admin" ||
    role === "Support Admin" ||
    role === "Platform admin" ||
    role === "resolver"
  );
}

export function useDuviCoreAdminSupportTicketDetail() {
  const { id } = useParams<{ id: string }>();
  const toast = useDuviCoreAdminToast();
  const { data, isLoading, refetch } = useDuviCoreAdminQuery(
    () => ticketsApi.get(id!),
    [id],
    { enabled: !!id },
  );

  const ticket = data?.ticket;
  const messages = data?.messages ?? [];

  const [replyText, setReplyText] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setPriority(ticket.priority);
    }
  }, [ticket]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !id) return;
    setIsSending(true);
    const internal = isInternalNote;
    try {
      await ticketsApi.addMessage(id, {
        content: replyText.trim(),
        is_internal: internal,
      });
      setReplyText("");
      setIsInternalNote(false);
      toast(
        "success",
        internal ? "Internal note added" : "Reply sent",
        internal
          ? "Visible to platform admins only."
          : "Your reply was sent to the submitter.",
      );
      refetch();
    } catch {
      // errors surfaced via the API client / session handling
    } finally {
      setIsSending(false);
    }
  };

  const handleResolve = async () => {
    if (!id) return;
    setIsResolving(true);
    try {
      await ticketsApi.resolve(id);
      toast("success", "Ticket resolved", `${id} marked as resolved.`);
      refetch();
    } catch {
      // ignore
    } finally {
      setIsResolving(false);
    }
  };

  const handleClose = async () => {
    if (!id) return;
    setIsClosing(true);
    try {
      await ticketsApi.close(id);
      toast("info", "Ticket closed", `${id} is now closed.`);
      refetch();
    } catch {
      // ignore
    } finally {
      setIsClosing(false);
    }
  };

  return {
    id,
    ticket,
    messages,
    isLoading,
    replyText,
    setReplyText,
    isInternalNote,
    setIsInternalNote,
    isSending,
    isResolving,
    isClosing,
    status,
    setStatus,
    priority,
    setPriority,
    handleSendReply,
    handleResolve,
    handleClose,
  };
}
